// สมุดสัตว์ (Animal-dex): ชนิดที่เคยเกิดเป็น สะสมไว้ในเบราว์เซอร์นี้ (docs/PLAN.md เฟส 3)

import { useMemo, useState } from "react";
import { GROUPS, isGroupEnabled } from "../../data/groups";
import { IUCN_TH, THREATENED } from "../../data/iucn";
import { DEATH_CAUSE_TH, formatAge } from "../../engine/text";
import type { DeathCause } from "../../engine/types";
import { dexList, type Dex, type DexEntry } from "../../shared/dex";
import { fmt, groupNames } from "../names";

interface Props {
  dex: Dex;
  busy: boolean;
  error: string | null;
  onPlay: (id: number) => void; // เกิดเป็นชนิดนี้อีกครั้ง (seed ใหม่)
  onBack: () => void;
}

type Sort = "recent" | "name" | "age" | "offspring";
const SORTS: Record<Sort, { label: string; compare: (a: DexEntry, b: DexEntry) => number }> = {
  recent: { label: "ล่าสุด", compare: (a, b) => b.lastSeen.localeCompare(a.lastSeen) },
  name: { label: "ชื่อ", compare: (a, b) => (a.nameTh ?? a.sci).localeCompare(b.nameTh ?? b.sci, "th") },
  age: { label: "อายุยืนสุด", compare: (a, b) => b.bestAgeDays - a.bestAgeDays },
  offspring: { label: "ลูกมากสุด", compare: (a, b) => b.bestOffspring - a.bestOffspring },
};

const ENABLED_GROUPS = GROUPS.filter(isGroupEnabled);
const SUBGROUP_TOTAL = ENABLED_GROUPS.reduce((n, g) => n + g.subgroups.length, 0);

function topCause(e: DexEntry): DeathCause | null {
  const list = Object.entries(e.causes) as [DeathCause, number][];
  return list.length ? list.sort((a, b) => b[1] - a[1])[0][0] : null;
}

function Entry({ e, busy, onPlay }: { e: DexEntry; busy: boolean; onPlay: (id: number) => void }) {
  const names = groupNames(e.subgroupId);
  const cause = topCause(e);
  return (
    <li className="card dex-entry">
      <div className="dex-title">
        <b>{e.nameTh ?? e.sci}</b>
        {e.iucn && <span className={THREATENED.has(e.iucn) ? "badge warn" : "badge"}>{IUCN_TH[e.iucn]}</span>}
      </div>
      <p className="sci small">{e.sci}</p>
      <p className="small muted">
        {names.group} · {names.subgroup} · เจอครั้งแรก {new Date(e.firstSeen).toLocaleDateString("th-TH", { day: "numeric", month: "short", year: "2-digit" })}
      </p>
      {e.lives > 0 ? (
        <p className="small">
          เล่น {fmt(e.lives)} ชีวิต · รอดจนโต {fmt(e.adults)} · อายุยืนสุด {formatAge(e.bestAgeDays)} · ลูกมากสุด {fmt(e.bestOffspring)}
          {e.maxGeneration > 1 && ` · สายตระกูล ${fmt(e.maxGeneration)} รุ่น`}
          {e.farthestKm >= 10 && ` · เดินทางไกลสุด ${fmt(e.farthestKm)} กม.`}
          {cause && ` · ตายบ่อยสุดเพราะ${DEATH_CAUSE_TH[cause]}`}
        </p>
      ) : (
        <p className="small muted">ยังเล่นชีวิตนี้ไม่จบ</p>
      )}
      <button className="btn" onClick={() => onPlay(e.id)} disabled={busy}>
        เกิดเป็นชนิดนี้อีกครั้ง
      </button>
    </li>
  );
}

export function Dex({ dex, busy, error, onPlay, onBack }: Props) {
  const entries = useMemo(() => dexList(dex), [dex]);
  const [group, setGroup] = useState<string | null>(null);
  const [sort, setSort] = useState<Sort>("recent");
  const subgroupsFound = new Set(entries.map((e) => e.subgroupId));
  const shown = entries.filter((e) => !group || e.groupId === group).sort(SORTS[sort].compare);

  return (
    <main className="screen">
      <section className="card">
        <h1 style={{ fontSize: "1.4rem" }}>สมุดสัตว์</h1>
        <p>
          สะสมแล้ว <b>{fmt(entries.length)}</b> ชนิด · ครบ <b>{fmt(subgroupsFound.size)}</b> จาก {fmt(SUBGROUP_TOTAL)} กลุ่มย่อย
        </p>
        <p className="small muted">เก็บไว้ในเบราว์เซอร์นี้เท่านั้น ถ้าล้างข้อมูลเว็บหรือเปลี่ยนเครื่อง สมุดจะเริ่มใหม่</p>
      </section>

      <section className="card dex-groups">
        {ENABLED_GROUPS.map((g) => {
          const found = g.subgroups.filter((s) => subgroupsFound.has(s.id));
          return (
            <div key={g.id} className="dex-group">
              <p className="small">
                <b>{g.nameTh}</b>{" "}
                <span className="muted">
                  {found.length}/{g.subgroups.length}
                </span>
              </p>
              <div className="dex-subgroups">
                {g.subgroups.map((s) => (
                  <span key={s.id} className={subgroupsFound.has(s.id) ? "chip found" : "chip"} title={s.nameTh}>
                    {subgroupsFound.has(s.id) ? s.nameTh : "?"}
                  </span>
                ))}
              </div>
            </div>
          );
        })}
      </section>

      {entries.length > 0 && (
        <div className="dex-filters">
          <div className="dex-subgroups" role="group" aria-label="กรองตามกลุ่ม">
            <button className={!group ? "chip found" : "chip"} onClick={() => setGroup(null)}>
              ทั้งหมด
            </button>
            {ENABLED_GROUPS.filter((g) => entries.some((e) => e.groupId === g.id)).map((g) => (
              <button key={g.id} className={group === g.id ? "chip found" : "chip"} onClick={() => setGroup(g.id)}>
                {g.nameTh}
              </button>
            ))}
          </div>
          <label className="small">
            เรียงตาม{" "}
            <select value={sort} onChange={(e) => setSort(e.target.value as Sort)}>
              {Object.entries(SORTS).map(([k, v]) => (
                <option key={k} value={k}>
                  {v.label}
                </option>
              ))}
            </select>
          </label>
        </div>
      )}

      {error && <p className="error">{error}</p>}
      {entries.length === 0 ? (
        <p className="muted">ยังไม่มีสัตว์ในสมุด เกิดเป็นสัตว์ตัวแรกแล้วชนิดนั้นจะถูกเก็บไว้ที่นี่</p>
      ) : (
        <ul className="dex-list">
          {shown.map((e) => (
            <Entry key={e.id} e={e} busy={busy} onPlay={onPlay} />
          ))}
        </ul>
      )}

      <div className="actions">
        <button className="btn block" onClick={onBack}>
          กลับหน้าแรก
        </button>
      </div>
    </main>
  );
}
